"use client"

import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import IframeModal from "@/components/ui-library-new/iframe/IframeModal"
import Richtext from "@/components/ui-library/richtext/Richtext"
import { MAPS_API_KEY } from "@/config/config"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import { VaccinationClinicWithHours } from "@/storyblok/vaccinationRelations"
import { VaccinationClinicInfoCardSectionStoryblok } from "@/types/component-types"
import { hasRichtextContent } from "@/utils/richTextToPlain"
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc"
import { useState } from "react"

export const VaccinationClinicInfoCardSection = ({
  blok,
  vaccination_clinic
}: {
  blok: BlokProps<VaccinationClinicInfoCardSectionStoryblok>["blok"]
  vaccination_clinic: VaccinationClinicWithHours
}) => {
  const { title } = blok
  const { opening_hours_content, deviating_opening_hours, address, extra_opening_hours } = vaccination_clinic
  const [openGoogleMaps, setOpenGoogleMaps] = useState(false)

  /**
   * `address` already ends with the clinic's locality, so it is the whole displayable address.
   * `city` is deliberately NOT appended: it holds the broader ort used for searching — Arninge
   * Station displays "Täby" but answers a search for "Stockholm" — so joining the two would render
   * "…, Täby, Stockholm". It stays in the overview's `search_criterias`, which reads the field
   * directly rather than the rendered string.
   */
  /**
   * A real destination behind the in-page map: hovering shows where it goes, and ⌘/ctrl/middle click
   * opens Google Maps proper in a new tab. A plain click keeps the reader here and opens the modal,
   * the same bargain the booking button makes.
   */
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`

  const handleOnMapClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    setOpenGoogleMaps(true)
  }

  return (
    <div
      {...storyblokEditable(blok as SbBlokData)}
      className="mx-auto mt-5 w-full max-w-[1155px] px-4 lg:mt-[17px] lg:px-8"
    >
      <CardBasic
        variant="softElevated"
        className="min-h-0 gap-2 p-4 leading-[14px] lg:p-[34px] lg:[&__p]:font-normal [&_h2]:mb-3 [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:leading-[22px] [&_h2]:tracking-[-0.045em] lg:[&_h2]:text-[20px] lg:[&_h2]:font-semibold lg:[&_h2]:leading-[27px] [&_p]:mb-0 [&_p]:text-sm [&_p]:leading-5 [&_p]:tracking-normal [&_p]:text-neutral-50 lg:[&_p]:text-base lg:[&_p]:leading-[23px]"
      >
        <h2 className="text-[16px] font-semibold leading-[22px] tracking-[-0.045em] lg:text-[20px] lg:leading-[27px]">
          {title}
        </h2>
        <Richtext key="opening_hours_content" content={opening_hours_content} />
        {hasRichtextContent(deviating_opening_hours) && (
          <div>
            <strong className="text-sm text-neutral-50 xl:text-base">Avvikande öppettider:</strong>
            <Richtext className="m-0 mt-5" key="deviating_opening_hours_content" content={deviating_opening_hours} />
          </div>
        )}
        {hasRichtextContent(extra_opening_hours) && (
          <div>
            <strong className="text-sm text-neutral-50 xl:text-base">Extra öppet:</strong>
            <Richtext className="m-0 mt-5" key="extra_opening_hours_content" content={extra_opening_hours} />
          </div>
        )}

        {address && (
          <>
            <div className="mt-2 flex flex-col gap-1">
              <strong className="text-sm text-neutral-50 xl:text-base">Adress:</strong>
              <a
                href={mapsHref}
                onClick={handleOnMapClick}
                target="_blank"
                rel="noreferrer"
                className="text-left text-[14px] text-neutral-50 underline decoration-1 underline-offset-1 hover:cursor-pointer hover:text-primary-50 lg:text-base lg:leading-[26px]"
              >
                {address}
              </a>
            </div>

            {openGoogleMaps && (
              <IframeModal
                src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${encodeURIComponent(address)}`}
                onClose={() => setOpenGoogleMaps(false)}
                useCloseButton
                modalClassName="p-0"
              />
            )}
          </>
        )}
      </CardBasic>
    </div>
  )
}
